/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { createContext, useContext, useCallback } from 'react';
import { useState } from 'react';

import { ThemeProvider } from 'styled-components';
import { setCookie } from 'nookies';
import { cookies } from '~/cookies';
import { ChakraProvider, useToast } from '@chakra-ui/react';

import { LoginContainer } from '~/components/LoginContainer';
import { ErrorContainer } from '~/components/ErrorContainer';

import { api, setDefaultAuthToken } from '~/services/api';

import { FCWithChildren } from '~/types/FCWithChildren';
import { ISigninResponse } from '~/interfaces/ISigninResponse';
import { IAppConfig } from '~/interfaces/IAppConfig';

import { theme } from '~/styles/theme';

interface IAppContext {
  readonly config: IAppConfig;
  readonly error?: string;
  readonly putError: (_error: string) => void;
}

const AppContext = createContext<IAppContext>({} as IAppContext);

interface IAppProviderProps {
  readonly tag: string;
}

const AppProvider: FCWithChildren<IAppProviderProps> = ({ children, tag }) => {
  const [error, setError] = useState<string>();
  const [config, setConfig] = useState<IAppConfig>();
  const toast = useToast({
    position: 'top-right',
    duration: 25000,
  });

  const putError = useCallback((_error: string) => {
    setError(_error);
  }, []);

  const signin = useCallback(
    async (password: string) => {
      try {
        const encodedConfigurationCredentials = Buffer.from(
          `${password}:${tag}`
        ).toString('base64');

        const { data } = await api.post<ISigninResponse>(
          '/auth/signin',
          {
            grantType: 'configuration_credentials',
          },
          {
            headers: {
              Authorization: `Basic ${encodedConfigurationCredentials}`,
            },
          }
        );

        const { accessToken, configuration } = data;
        setCookie(undefined, cookies.AUTH_TOKEN, accessToken, {
          maxAge: 60 * 60 * 24, // 24 hours
          path: '/',
        });

        setDefaultAuthToken(accessToken);
        setConfig(configuration);
      } catch {
        toast({
          status: 'error',
          title: 'Senha de acesso incorreta',
        });
      }
    },
    [toast, tag]
  );

  return (
    <AppContext.Provider value={{ config, error, putError }}>
      <ChakraProvider>
        <ThemeProvider
          theme={{
            ...theme,
            colors: config?.colors || theme.colors,
          }}
        >
          {error ? (
            <ErrorContainer error={error} />
          ) : !config ? (
            <LoginContainer signin={signin} />
          ) : (
            children
          )}
        </ThemeProvider>
      </ChakraProvider>
    </AppContext.Provider>
  );
};

const useAppConfig = () => useContext(AppContext);

export { useAppConfig, AppProvider };
